import React, {
    createRef,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { MathJax } from "better-react-mathjax";
import { toast } from "react-toastify";

import ActionButton from "../formInputs/buttons/ActionButton";
import ElementaryProgressReportDao from "../../dao/ElementaryProgressReportDao";
import Icon from "../icon/Icon";
import LoadingSvg from "../loadingSvg/LoadingSvg";
import RatingTableBody from "./RatingTableBody";
import RatingTableModal from "./RatingTableModal";
import useFlattenedStudents from "../../hooks/useFlattenedStudents";
import useHydrateRatings from "../../hooks/useHydrateRatings";
import useRatingSubmission from "../../hooks/useRatingSubmission";
import useStudentImages from "../../hooks/useStudentImages";

import {
    extractStudentGradingProfileId,
    getFormattedHeaderAndTitle,
    getGradingCriteriaId,
    RATING_DISPLAY_LABELS,
    RATING_OPTIONS
} from "../../utils/InputRatingUtils";
import { removeHiddenChars } from "../../utils/removeHiddenChars";

import "../../styles/RatingTable.scss";

/**
 * RatingTable component renders a two-part table interface for teachers to rate students
 * and enter student-specific comments based on standards, GLEs, and Evidence Outcomes.
 *
 * It supports:
 * - Bulk and individual rating of content items (standards/GLEs/EOs)
 * - Per-student comment entry for a selected grading criteria
 * - Auto-saving after input changes
 * - Sorting, scrolling, and dynamic layout adjustments
 * - Syncing and submitting to backend via `ElementaryProgressReportDao`
 *
 * @component
 * @param {Object} props
 * @param {Object[]} props.existingRatingsData
 * @param {Object[]} props.existingStudentComments
 * @param {Function} props.fetchRatings
 * @param {Object[]} props.groupData
 * @param {Boolean} props.hasChanges
 * @param {Object} props.selectedTemplate
 * @param {Object} props.selectedEpr
 * @param {Function} props.setExistingRatingsData
 * @param {Function} props.setExistingStudentComments
 * @param {Function} props.setHasChanges
 * @param {string} props.token
 * @param {Object} props.userDetails
 */
const RatingTableInner = (props, ref) => {
    const {
        existingRatingsData,
        existingStudentComments,
        fetchRatings,
        fetchSelectedGroupInfo,
        groupData,
        hasChanges,
        mode,
        selectedTemplate,
        selectedEpr,
        setExistingRatingsData,
        setExistingStudentComments,
        setHasChanges,
        token,
        userDetails
    } = props;

    useImperativeHandle(ref, () => ({
        submitChanges: handleSubmit
    }));

    const [activeColumnIndex, setActiveColumnIndex] = useState(0);
    const [ratings, setRatings] = useState({});
    const [ratingsLoaded, setRatingsLoaded] = useState(false);
    const [showComments, setShowComments] = useState(false);
    const [comments, setComments] = useState({});
    const [sortConfig, setSortConfig] = useState(() => ({
        key: mode === "GROUPS" ? "groupName" : "lastName",
        direction: "ascending"
    }));
    const [activeCommentContext, setActiveCommentContext] = useState(null);
    const [commentModalOpen, setCommentModalOpen] = useState(false);

    const autoSaveTimeout = useRef(null);
    const columnRefs = useRef([]);
    const fixedRef = useRef();
    const scrollRef = useRef();

    const flattenedStudents = useFlattenedStudents({ mode, groupData, selectedEpr, existingRatingsData });

    const handleOpenCommentModal = (student, item) => {
        const isNew = !student.contentRatings.find((cr) => cr.templateContentId === item.templateContentId);
        if (isNew && hasChanges) return;
        openCommentModal(student, item);
    };
    const derivedExistingRatingsData = useMemo(() => {
        if (mode === "GROUPS" && groupData?.length) {
            return groupData.flatMap((group) =>
                group.groupStudentContents.map((student) => ({
                    key: `${group.groupId}-${student.gradingProfileStudentId}`,
                    comment: null,
                    contentRatings: student.contentRatings || [],
                    contentStudentComments: student.contentStudentComments || null,
                    gradingProfile: group.gradingProfileId,
                    sectionId: student.sectionId,
                    studentNumber: student.studentNumber,
                    teacherName: group.teacherName || ""
                }))
            );
        }
        return existingRatingsData;
    }, [existingRatingsData, groupData, mode]);

    const students = useMemo(() => {
        // used to identify duplicate students in groups
        const seen = new Map();

        flattenedStudents.forEach((student) => {
            const key = student.studentNumber;
            if (!seen.has(key)) seen.set(key, []);
            seen.get(key).push(student);
        });

        const result = [];

        seen.forEach((instances) => {
            const editable = instances.find((s) => s.groupPermission !== "VIEW");
            const selected = editable || instances[0];

            result.push({
                ...selected,
                isDisabled: !editable // Only disable if all instances are view-only
            });
        });

        return result.sort((a, b) => {
            if (!sortConfig.key && mode === "GROUPS") {
                const groupCompare = a.groupName?.localeCompare(b.groupName || "") || 0;
                if (groupCompare !== 0) return groupCompare;
                return a.lastName?.localeCompare(b.lastName || "") || 0;
            }

            const aVal = a[sortConfig.key]?.toLowerCase?.() || "";
            const bVal = b[sortConfig.key]?.toLowerCase?.() || "";
            return sortConfig.direction === "ascending" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        });
    }, [flattenedStudents, mode, sortConfig]);

    const { images: studentImages, loading: imagesLoading } = useStudentImages(
        students,
        selectedEpr.schoolYearKey,
        token
    );

    const openCommentModal = (student, contentItem) => {
        if (hasChanges) {
            handleSubmit();
        }
        const ratingKey = `${student.key}_${contentItem.templateContentId}`;
        const localRating = ratings[ratingKey] || "";

        const existingRating = derivedExistingRatingsData
            .find((r) => r.key === student.key)
            ?.contentRatings?.find((r) => r.templateContentId === contentItem.templateContentId);

        setActiveCommentContext({
            student,
            contentItem,
            rating: localRating || existingRating?.rating || "",
            comment: existingRating?.comment || "",
            lastRatedGuid: existingRating?.lastRatedGuid,
            key: existingRating?.key
        });
        setCommentModalOpen(true);
    };

    const closeCommentModal = () => {
        setCommentModalOpen(false);
        setActiveCommentContext(null);
    };
    const renderStudentImage = useCallback(
        (student) => {
            const imageUrl = studentImages[student.studentNumber];
            return imageUrl ? (
                <img
                    src={imageUrl}
                    alt={`Photo of ${student.firstName} ${student.lastName}`}
                    style={{ width: "75px", height: "100px", borderRadius: "5%", objectFit: "cover" }}
                />
            ) : (
                <Icon iconName="NO_STUDENT_IMAGE" fill="#19608F" height={100} width={75} />
            );
        },
        [studentImages]
    );

    const requestSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "ascending" ? "descending" : "ascending"
        }));
    };

    const renderSortIcons = (columnKey) => {
        if (sortConfig.key === columnKey) {
            if (sortConfig.direction === "ascending") {
                return (
                    <span className="icon-container">
                        <i className="bi bi-caret-up-fill" />
                        <i className="bi bi-caret-down" style={{ marginBottom: "5px" }} />
                    </span>
                );
            } else {
                return (
                    <span className="icon-container">
                        <i className="bi bi-caret-up" />
                        <i className="bi bi-caret-down-fill" style={{ marginBottom: "5px" }} />
                    </span>
                );
            }
        }
        return (
            <span className="icon-container">
                <i className="bi bi-caret-up" />
                <i className="bi bi-caret-down" style={{ marginBottom: "5px" }} />
            </span>
        );
    };

    const contentItems = useMemo(() => {
        const items = [];
        selectedTemplate?.cdeStandards?.forEach((standard) => {
            if (standard.reportFlag) {
                items.push({
                    ...standard,
                    contentType: "CDESTANDARD",
                    templateContentId: standard.templateContentId,
                    label: standard.standardName,
                    title: `Standard ${standard.standardName.slice(0, 1)}:`,
                    header: standard.standardName.slice(0, 25)
                });
            }
            standard.gles?.forEach((gle) => {
                if (gle.reportFlag) {
                    items.push({
                        ...gle,
                        contentType: "GLE",
                        templateContentId: gle.templateContentId,
                        label: gle.gleName,
                        header: `${gle.gleName.slice(0, 25)}`,
                        title: gle.gleShortName
                    });
                }
                gle.evidenceOutcomes?.forEach((eo) => {
                    const { header, shortTitle } = getFormattedHeaderAndTitle(eo.body);
                    if (eo.reportFlag) {
                        items.push({
                            ...eo,
                            contentType: "EVIDENCE_OUTCOME",
                            templateContentId: eo.templateContentId,
                            label: eo.body,
                            header,
                            title: shortTitle
                        });
                    }
                    eo.evidenceOutcomeChildList?.forEach((eoc) => {
                        const { header, shortTitle } = getFormattedHeaderAndTitle(eoc.body);

                        if (eoc.reportFlag) {
                            items.push({
                                ...eoc,
                                contentType: "EVIDENCE_OUTCOME_CHILD",
                                templateContentId: eoc.templateContentId,
                                label: eoc.body,
                                header,
                                title: shortTitle
                            });
                        }
                    });
                });
            });
        });
        return items;
    }, [selectedTemplate]);

    const handleToggleComments = () => {
        if (hasChanges) {
            handleSubmit();
        }
        setShowComments((prev) => !prev);
    };

    const handleRatingChange = useCallback(
        (studentKey, itemId, rating) => {
            const key = `${studentKey}_${itemId}`;
            setRatings((prev) => ({ ...prev, [key]: rating }));
            setHasChanges(true);
        },
        [setHasChanges]
    );

    const handleBulkRating = (itemId, rating) => {
        const newRatings = { ...ratings };
        students.forEach((s) => {
            if (s.groupPermission !== "VIEW") {
                const key = `${s.key}_${itemId}`;
                const currentValue = newRatings[key];
                const isEmpty = currentValue === null || currentValue === undefined || currentValue === "";
                if (isEmpty || rating === null || rating === "") {
                    newRatings[key] = rating;
                }
            }
        });
        setRatings(newRatings);
        setHasChanges(true);
    };

    const handleCommentChange = useCallback(
        (studentGradingProfileId, comment, gradingCriteria) => {
            const key = `${studentGradingProfileId}_${gradingCriteria}`;
            setComments((prev) => ({ ...prev, [key]: comment }));
            setHasChanges(true);
        },
        [setHasChanges]
    );

    const handleRatingComment = () => {
        const { student, contentItem, rating, comment, key, lastRatedGuid } = activeCommentContext;

        const gradingCriteria = getGradingCriteriaId(student, groupData, selectedEpr, selectedTemplate, mode);

        const payload = {
            key,
            gradingProfileStudentId: extractStudentGradingProfileId(student.key),
            gradingCriteria: Number(gradingCriteria),
            templateContentId: Number(contentItem.templateContentId),
            contentType: contentItem.contentType,
            rating: rating === "DOES NOT MEET" ? "DOES_NOT_MEET" : rating,
            comment: removeHiddenChars(comment.trim()),
            personalizedLearningPlan: false,
            lastRatedGuid: Number(lastRatedGuid)
        };

        ElementaryProgressReportDao({
            action: "updateContentRating",
            ratingKey: key,
            data: payload,
            token
        }).then((response) => {
            if (response && response.data?.payload) {
                toast.success("Comment saved.");
                if (mode === "GROUPS") {
                    fetchSelectedGroupInfo();
                }
                fetchRatings();
            }

            closeCommentModal();
        });
    };

    /**
     * Submits either student comments or student ratings to the backend.
     *
     * - If `showComments` is true, prepares a filtered comment payload including only changed comments.
     *   Each object includes: { key, gradingProfileStudentId, gradingCriteria, comment }
     *
     * - If `showComments` is false, builds a rating submission array using `mapRatingSubmission`.
     *   Each object includes student ratings for selected contentItems.
     *
     * Both flows call `ElementaryProgressReportDao` with the appropriate action and payload,
     * update local state with response data, and show a success toast.
     *
     * Dependencies: ratings, comments, contentItems, existingRatingsData, existingStudentComments,
     * selectedEpr, selectedTemplate, setExistingRatingsData, setExistingStudentComments, token, userDetails, students
     */
    const handleSubmit = useRatingSubmission({
        comments,
        contentItems,
        derivedExistingRatingsData,
        existingRatingsData,
        existingStudentComments,
        fetchSelectedGroupInfo,
        groupData,
        mode,
        ratings,
        selectedEpr,
        selectedTemplate,
        setExistingRatingsData,
        setExistingStudentComments,
        setHasChanges,
        setRatingsLoaded,
        showComments,
        students,
        token,
        userDetails
    });

    useHydrateRatings({
        derivedExistingRatingsData,
        existingStudentComments,
        groupData,
        mode,
        setComments,
        setRatings,
        setRatingsLoaded
    });

    /**
     * Debounces rating or comment changes and triggers auto-save after 4 seconds.
     * Clears the timer on unmount or additional changes before timeout.
     */
    useEffect(() => {
        if (!hasChanges) return;

        if (autoSaveTimeout.current) {
            clearTimeout(autoSaveTimeout.current);
        }

        autoSaveTimeout.current = setTimeout(() => {
            handleSubmit();
            setHasChanges(false);
        }, 4000);

        return () => clearTimeout(autoSaveTimeout.current);
    }, [handleSubmit, hasChanges, ratings, setHasChanges]);

    /**
     * Synchronizes vertical scrolling between the fixed student info columns
     * and the scrollable rating columns to maintain row alignment.
     */
    useEffect(() => {
        const fixed = fixedRef.current;
        const scroll = scrollRef.current;

        if (!fixed || !scroll) return;

        const onFixedScroll = () => {
            scroll.scrollTop = fixed.scrollTop;
        };

        const onScroll = () => {
            fixed.scrollTop = scroll.scrollTop;
        };

        scroll.addEventListener("scroll", onScroll);
        fixed.addEventListener("scroll", onFixedScroll);

        return () => {
            scroll.removeEventListener("scroll", onScroll);
            fixed.removeEventListener("scroll", onFixedScroll);
        };
    }, [ratingsLoaded, imagesLoading]);

    /**
     * Regenerates a ref object for each content column when the template changes.
     * Used for scrolling into view and DOM interaction.
     */
    useEffect(() => {
        columnRefs.current = contentItems.map(() => createRef());
    }, [contentItems]);

    /**
     * Scrolls horizontally to the active rating/comment column so it’s fully in view.
     * Triggered when Next/Prev column navigation is used.
     */
    useEffect(() => {
        const activeRef = columnRefs.current[activeColumnIndex];
        if (activeRef?.current && scrollRef.current) {
            const scrollContainer = scrollRef.current;
            const activeEl = activeRef.current;

            const scrollLeft = activeEl.offsetLeft - scrollContainer.offsetLeft;
            const scrollRightEdge = scrollLeft + activeEl.offsetWidth;

            if (scrollLeft < scrollContainer.scrollLeft) {
                scrollContainer.scrollTo({ left: scrollLeft - 20, behavior: "smooth" });
            } else if (scrollRightEdge > scrollContainer.scrollLeft + scrollContainer.clientWidth) {
                scrollContainer.scrollTo({
                    left: scrollRightEdge - scrollContainer.clientWidth + 20,
                    behavior: "smooth"
                });
            }
        }
    }, [activeColumnIndex]);

    if (!selectedTemplate || !selectedEpr) return null;

    return (
        <section>
            {!ratingsLoaded && <LoadingSvg />}

            <div className={classNames("rating-column-description-wrapper", { hide: showComments })}>
                <h5 className="rating-column-desc-title">Selected Content Item Description:</h5>
                <div className="rating-column-description">
                    <strong>{contentItems[activeColumnIndex]?.title || ""}</strong>
                    <MathJax hideUntilTypeset={"first"} tabIndex={-1}>
                        <p dangerouslySetInnerHTML={{ __html: contentItems[activeColumnIndex]?.label }} />
                    </MathJax>
                </div>
            </div>

            <div className="rating-table-btn-group">
                <ActionButton
                    label={showComments ? "Hide Student Comments" : "Show Student Comments"}
                    onClick={() => handleToggleComments()}
                />
                <div className="column-nav">
                    <ActionButton
                        className="action-button-80"
                        disable={showComments}
                        label="← Prev"
                        onClick={() => setActiveColumnIndex(Math.max(0, activeColumnIndex - 1))}
                    />
                    <ActionButton
                        className="action-button-80"
                        disable={showComments}
                        label="Next →"
                        onClick={() => setActiveColumnIndex(Math.min(contentItems.length - 1, activeColumnIndex + 1))}
                    />
                </div>
            </div>
            <div className="ratings-table-wrapper">
                <div className="table-split-wrapper">
                    <div className="fixed-columns" ref={fixedRef}>
                        <table className="rating-table">
                            <thead>
                                <tr>
                                    {mode === "GROUPS" && (
                                        <th>
                                            <button
                                                type="button"
                                                onClick={() => requestSort("groupName")}
                                                className="table-header-button"
                                                aria-label="Sort by group name"
                                            >
                                                Group Name {renderSortIcons("groupName")}
                                            </button>
                                        </th>
                                    )}
                                    <th></th>
                                    <th>
                                        <button
                                            type="button"
                                            onClick={() => requestSort("lastName")}
                                            className="table-header-button"
                                            aria-label="Sort by last name"
                                        >
                                            Last Name {renderSortIcons("lastName")}
                                        </button>
                                    </th>
                                    <th>
                                        <button
                                            type="button"
                                            onClick={() => requestSort("firstName")}
                                            className="table-header-button"
                                            aria-label="Sort by first name"
                                        >
                                            First Name {renderSortIcons("firstName")}
                                        </button>
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {students.map((student) => (
                                    <tr
                                        key={student.key}
                                        className={
                                            student.isShared && student.groupPermission === "VIEW"
                                                ? "view-only-row"
                                                : ""
                                        }
                                    >
                                        {mode === "GROUPS" && (
                                            <td>
                                                <div className="group-cell">
                                                    <b>{student.groupName}</b>{" "}
                                                    {student.isShared && (
                                                        <div className="group-text">{student.groupHomeroomTeacher}</div>
                                                    )}
                                                    {student.isShared && student.groupPermission === "VIEW" && (
                                                        <div className="group-text view-text">View Only</div>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                        <td>{renderStudentImage(student)}</td>
                                        <td>{student.lastName}</td>
                                        <td>{student.firstName}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="scrollable-columns" ref={scrollRef}>
                        <table className="rating-table" style={showComments ? { width: "100%" } : {}}>
                            <thead>
                                {showComments ? (
                                    <tr>
                                        <th style={{ fontSize: "1rem" }}>Comments</th>
                                    </tr>
                                ) : (
                                    <tr>
                                        {contentItems.map((item, index) => (
                                            <th
                                                key={item.templateContentId}
                                                className={index === activeColumnIndex ? "active-rating-col" : ""}
                                                ref={columnRefs.current[index]}
                                            >
                                                <div
                                                    className="clickable-header"
                                                    onClick={() => setActiveColumnIndex(index)}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" || e.key === " ")
                                                            setActiveColumnIndex(index);
                                                    }}
                                                >
                                                    {item.header || item.label.split(" ").slice(0, 3).join(" ")}
                                                </div>
                                                <select
                                                    value=""
                                                    onChange={(e) =>
                                                        handleBulkRating(item.templateContentId, e.target.value || null)
                                                    }
                                                >
                                                    <option value="">Rate for All</option>
                                                    {RATING_OPTIONS.map((opt) => (
                                                        <option key={opt} value={opt}>
                                                            {RATING_DISPLAY_LABELS[opt] || opt}
                                                        </option>
                                                    ))}
                                                    <option value="">Clear Ratings</option>
                                                </select>
                                            </th>
                                        ))}
                                    </tr>
                                )}
                            </thead>
                            <RatingTableBody
                                activeColumnIndex={activeColumnIndex}
                                comments={comments}
                                contentItems={contentItems}
                                derivedExistingRatingsData={derivedExistingRatingsData}
                                getGradingCriteriaId={getGradingCriteriaId}
                                groupData={groupData}
                                handleCommentChange={handleCommentChange}
                                handleOpenCommentModal={handleOpenCommentModal}
                                handleRatingChange={handleRatingChange}
                                hasChanges={hasChanges}
                                mode={mode}
                                ratings={ratings}
                                selectedEpr={selectedEpr}
                                selectedTemplate={selectedTemplate}
                                showComments={showComments}
                                students={students}
                            />
                        </table>
                    </div>
                </div>
            </div>
            <div className="save-btn-ratings">
                <ActionButton label="Save" onClick={handleSubmit} />
            </div>
            <RatingTableModal
                open={commentModalOpen}
                onClose={closeCommentModal}
                onSave={handleRatingComment}
                context={activeCommentContext}
                setContext={setActiveCommentContext}
            />
        </section>
    );
};

const RatingTable = forwardRef(RatingTableInner);

RatingTableInner.propTypes = {
    existingRatingsData: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            gradingProfile: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            studentNumber: PropTypes.string,
            contentRatings: PropTypes.arrayOf(
                PropTypes.shape({
                    key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                    gradingCriteria: PropTypes.number,
                    templateContentId: PropTypes.number,
                    contentType: PropTypes.string,
                    rating: PropTypes.string,
                    comment: PropTypes.string,
                    personalizedLearningPlan: PropTypes.bool,
                    lastRatedGuid: PropTypes.number
                })
            )
        })
    ).isRequired,
    existingStudentComments: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            gradingProfileStudentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            gradingCriteria: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            comment: PropTypes.string
        })
    ).isRequired,
    fetchRatings: PropTypes.func.isRequired,
    fetchSelectedGroupInfo: PropTypes.func.isRequired,
    groupData: PropTypes.instanceOf(Array),
    hasChanges: PropTypes.bool,
    mode: PropTypes.string,
    selectedEpr: PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        schoolYearKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        gradingProfileStudents: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                studentNumber: PropTypes.string,
                firstName: PropTypes.string,
                lastName: PropTypes.string
            })
        ),
        gradingCriteriaList: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                parentTemplate: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            })
        )
    }).isRequired,
    selectedTemplate: PropTypes.shape({
        templateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        cdeStandards: PropTypes.arrayOf(
            PropTypes.shape({
                reportFlag: PropTypes.bool,
                templateContentId: PropTypes.number,
                standardName: PropTypes.string,
                gles: PropTypes.array
            })
        )
    }).isRequired,
    setExistingRatingsData: PropTypes.func.isRequired,
    setExistingStudentComments: PropTypes.func.isRequired,
    setHasChanges: PropTypes.func.isRequired,
    token: PropTypes.string.isRequired,
    userDetails: PropTypes.object.isRequired
};

RatingTableInner.defaultProps = {
    hasChanges: false,
    mode: "HOMEROOM"
};

export default RatingTable;
